import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';

/**
 * NotificationBell
 *
 * Props:
 *   api      – one of adminApi / vendorApi / clientApi (must expose getNotifications,
 *              markAllNotificationsRead, markNotificationRead)
 *   portal   – 'platform' | 'vendor' | 'client'  (used to build the navigate URL)
 */
export default function NotificationBell({ api, portal }) {
  const [open, setOpen]   = useState(false);
  const dropRef            = useRef(null);
  const navigate           = useNavigate();
  const qc                 = useQueryClient();
  const QUERY_KEY          = ['notifications', portal];

  // ── Poll every 30 s ──────────────────────────────────────────────────────
  const { data } = useQuery(
    QUERY_KEY,
    () => api.getNotifications().then(r => r.data ?? r),
    {
      refetchInterval:         30_000,
      refetchIntervalInBackground: true,
      staleTime:               0,
    }
  );

  const notifications = Array.isArray(data) ? data : [];
  const unread        = notifications.filter(n => !n.read).length;

  // ── Mark all read ────────────────────────────────────────────────────────
  const markAllMutation = useMutation(
    () => api.markAllNotificationsRead(),
    { onSuccess: () => qc.invalidateQueries(QUERY_KEY) }
  );

  // ── Mark one read ─────────────────────────────────────────────────────────
  const markOneMutation = useMutation(
    (id) => api.markNotificationRead(id),
    { onSuccess: () => qc.invalidateQueries(QUERY_KEY) }
  );

  // ── Click a notification → navigate + mark read ──────────────────────────
  const handleClick = useCallback((n) => {
    if (!n.read) markOneMutation.mutate(n.id);
    setOpen(false);

    // Build target URL based on portal and notification data
    if (portal === 'platform') {
      navigate(`/platform/finished-goods/${n.fgId}#notes`);
    } else if (portal === 'vendor') {
      navigate(`/vendor/assignments/${n.assignmentId}#notes`);
    } else {
      navigate(`/client/review/${n.fgId}#notes`);
    }
  }, [portal, navigate, markOneMutation]);

  // ── Close dropdown when clicking outside ────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Relative time helper ─────────────────────────────────────────────────
  function relativeTime(isoString) {
    const diff = Date.now() - new Date(isoString).getTime();
    const mins  = Math.floor(diff / 60_000);
    if (mins < 1)  return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  return (
    <div className="relative" ref={dropRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(v => !v)}
        className="relative flex items-center justify-center w-9 h-9 rounded-xl text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
        title="Notifications"
      >
        {/* Bell SVG */}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
          strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>

        {/* Unread badge */}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center
            bg-red-500 text-white text-[10px] font-bold rounded-full px-1 leading-none">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-11 w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-900">Notifications</span>
              {unread > 0 && (
                <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-semibold rounded-full">
                  {unread} new
                </span>
              )}
            </div>
            {unread > 0 && (
              <button
                onClick={() => markAllMutation.mutate()}
                disabled={markAllMutation.isLoading}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto max-h-[400px] divide-y divide-gray-50">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
                  className="h-8 w-8 mb-2 opacity-40">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
                </svg>
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3
                    ${!n.read ? 'bg-blue-50/60' : ''}`}
                >
                  {/* Dot indicator */}
                  <span className={`mt-1.5 flex-shrink-0 w-2 h-2 rounded-full
                    ${!n.read ? 'bg-blue-500' : 'bg-transparent'}`} />

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-snug ${!n.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                      {n.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                      <span className="font-mono">{n.fgNumber}</span>
                      <span>·</span>
                      <span>{relativeTime(n.createdAt)}</span>
                    </p>
                  </div>

                  {/* Arrow */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                    strokeLinecap="round" className="h-3.5 w-3.5 text-gray-300 flex-shrink-0 mt-1">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
